// deps
import React, { memo } from "react";

// styles
import {
    AddIcon,
    AddText,
    AddTip,
    TipAmountRow,
    TipChevronIcon,
    TipElement,
    TipSection,
    TipSuggestion,
    TipSuggestionitem,
    TipTextWrapper,
} from "../../styles";

// helpers
import { format, isEmpty } from "@swiggy-private/core-ui";
import { isEqual } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ChevronDownIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";
import { default as PlusIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/plus/16";

// constants
import { BILL_TEXTS } from "@dineout-payment-success-ui/constants/BillDetails";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";
import { CART_BILL_TIP_OTHER, CART_BILL_TIP_VALUES } from "@dineout-payment-success-ui/constants/CartTip";

// types
import { IBillEntries } from "@dineout-payment-success-ui/interfaces";

// components
import { BillDivider } from "../payment-success-bill-divider";
import { StyledText } from "../../../payment-success-banner/styles";

export interface ICartTipSectionProps {
    item: IBillEntries;
    addTip?: boolean;
    tipVisible: boolean;
    tipValue: number;
    selectedTipIndex: number | null;
    onAddTipClick: VoidFunction;
    onClearTip: VoidFunction;
    onTipSelected: (data: number | string, index: number) => void;
}

const CartTipSection: React.FC<ICartTipSectionProps> = ({
    item,
    addTip,
    tipVisible,
    tipValue,
    selectedTipIndex,
    onAddTipClick,
    onClearTip,
    onTipSelected,
}) => {
    const tipAmountText = item.rightElement?.text ?? "";
    const parsedTipAmount = Number(tipAmountText ? tipAmountText.replace(/[^\d]/g, "") : "");
    const showTipAmountRow = Boolean(addTip || tipValue || !isEmpty(tipAmountText));
    const showClearTip = Boolean((tipValue && addTip) || parsedTipAmount);
    const showTipSuggestions = Boolean(tipVisible && addTip);

    return (
        <>
            <TipSection>
                <TipElement>
                    <TipTextWrapper>
                        <AddTip data-testid="AddTipIcon" onClick={onAddTipClick}>
                            {!tipVisible ? (
                                <AddIcon>
                                    <GenericIcon Icon={PlusIcon} fillColor="primary" />
                                </AddIcon>
                            ) : (
                                <TipChevronIcon $isExpanded={tipVisible}>
                                    <GenericIcon Icon={ChevronDownIcon} fillColor="primary" />
                                </TipChevronIcon>
                            )}
                            <AddText>
                                <Text variant="V2.body2.neutral.bold" color="primary">
                                    {item.leftElement?.text || BILL_TEXTS.ADD_TIP}
                                </Text>
                            </AddText>
                        </AddTip>
                        {!isEmpty(item.leftSubText) && (
                            <Text
                                variant={
                                    item.leftSubTextStyles?.variant
                                        ? item.leftSubTextStyles.variant
                                        : "V2.overline.neutral.regular"
                                }
                                color={
                                    item.leftSubTextStyles?.color ? item.leftSubTextStyles.color : "text_Med_Emphasis"
                                }
                            >
                                {item.leftSubText}
                            </Text>
                        )}
                    </TipTextWrapper>

                    {showTipAmountRow ? (
                        <TipAmountRow>
                            {showClearTip ? (
                                <div onClick={onClearTip} data-testid="ClearTip">
                                    <StyledText variant="V2.body3.neutral.regular" color="primary">
                                        {BILL_TEXTS.CLEAR_TIP}
                                    </StyledText>
                                </div>
                            ) : null}
                            <StyledText
                                variant="V2.body2.neutral.medium"
                                color="text_High_Emphasis"
                                data-testid="TIPPING_AMOUNT"
                            >
                                {tipAmountText}
                            </StyledText>
                        </TipAmountRow>
                    ) : null}
                </TipElement>

                {showTipSuggestions && (
                    <TipSuggestion data-testid={"TipSuggestion"}>
                        {CART_BILL_TIP_VALUES.map((tipVal, index) => {
                            return (
                                <TipSuggestionitem
                                    tabIndex={index}
                                    selectedTipIndex={selectedTipIndex as number}
                                    key={index.toString()}
                                    data-testid={"TipItem"}
                                    onClick={() => onTipSelected(tipVal, index)}
                                >
                                    <Text
                                        variant="V2.body2.neutral.medium"
                                        color={selectedTipIndex === index ? "swiggy_One" : "text_High_Emphasis"}
                                    >
                                        {isEqual(tipVal, CART_BILL_TIP_OTHER)
                                            ? tipVal
                                            : format(INR_CURRENCY_SYMBOL_TEXT, "" + tipVal)}
                                    </Text>
                                </TipSuggestionitem>
                            );
                        })}
                    </TipSuggestion>
                )}
            </TipSection>
            {item.isBillDivider && <BillDivider />}
        </>
    );
};

export default memo(CartTipSection);
